import clsx from "clsx";
import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";
import { Field, controlClasses, describedBy } from "./Field";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  /** Fixed text after the control, e.g. a unit label. */
  suffix?: ReactNode;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, suffix, containerClassName, className, id, required, ...rest },
  ref,
) {
  const generated = useId();
  const inputId = id ?? generated;
  const control = (
    <input
      ref={ref}
      id={inputId}
      required={required}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy(inputId, hint, error)}
      className={clsx(controlClasses, error && "border-red-600", className)}
      {...rest}
    />
  );
  return (
    <Field
      id={inputId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={containerClassName}
    >
      {suffix ? (
        <div className="flex items-center gap-2">
          {control}
          <span className="shrink-0 text-sm text-ink-600 dark:text-ink-300">{suffix}</span>
        </div>
      ) : (
        control
      )}
    </Field>
  );
});
